import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import KeyIcon from "@material-ui/icons/VpnKeyOutlined";

import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 920, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  title: { fontWeight: 600, fontSize: 18, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4, marginBottom: theme.spacing(3) },
  section: { marginBottom: theme.spacing(3.5) },
  h: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 700, marginBottom: 10 },
  card: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: "14px 16px" },
  kv: { display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${theme.app.lineSoft}`, "&:last-child": { borderBottom: "none" } },
  kvK: { fontSize: 12.5, color: theme.app.textSecondary, width: 130, flexShrink: 0 },
  kvV: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  copy: { marginLeft: "auto", padding: 4, color: theme.app.textTertiary },
  tabs: { display: "flex", gap: 4, marginBottom: 0 },
  tab: { fontSize: 12.5, padding: "6px 12px", borderRadius: "8px 8px 0 0", cursor: "pointer", color: theme.app.textSecondary, fontFamily: theme.app.mono, border: `1px solid transparent`, borderBottom: "none" },
  tabOn: { background: "#1B222A", color: "#E8FFB3", border: `1px solid #1B222A` },
  code: { position: "relative", background: "#161B21", color: "#E6E9ED", borderRadius: "0 8px 8px 8px", padding: "14px 16px", fontFamily: theme.app.mono, fontSize: 12.5, lineHeight: 1.6, overflowX: "auto", whiteSpace: "pre", margin: 0 },
  codeCopy: { position: "absolute", top: 8, right: 8, color: "#9aa3ad", padding: 4 },
  scopes: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  scope: { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${theme.app.border}`, borderRadius: 8, fontSize: 12.5, color: theme.app.ink },
  scopeKey: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary, marginLeft: "auto" },
  steps: { counterReset: "s", listStyle: "none", padding: 0, margin: 0 },
  step: { position: "relative", padding: "8px 0 8px 30px", fontSize: 13, color: theme.app.textSecondary, lineHeight: 1.5, "&::before": { counterIncrement: "s", content: "counter(s)", position: "absolute", left: 0, top: 7, width: 19, height: 19, borderRadius: "50%", background: theme.app.dark, color: "#fff", fontSize: 11, fontFamily: theme.app.mono, display: "flex", alignItems: "center", justifyContent: "center" } },
}));

const copy = txt => {
  try {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado!");
  } catch (e) {
    /* clipboard blocked */
  }
};

const Overview = () => {
  const classes = useStyles();
  const history = useHistory();
  const [scopes, setScopes] = useState([]);
  const [lang, setLang] = useState("curl");

  const base = `${window.location.origin}/v1`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api-keys/scopes");
      setScopes(data.scopes || data || []);
    } catch (e) {
      /* silencioso: o overview funciona sem o catálogo */
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const snippets = {
    curl: `curl -X POST ${base}/calls \\
  -H "Authorization: Bearer SUA_CHAVE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+5519999998888",
    "from": "+5519888887777",
    "applicationId": "app_xxx",
    "variables": { "nome": "Maria" }
  }'`,
    node: `const res = await fetch("${base}/calls", {
  method: "POST",
  headers: {
    Authorization: "Bearer SUA_CHAVE",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    to: "+5519999998888",
    from: "+5519888887777",
    applicationId: "app_xxx",
    variables: { nome: "Maria" },
  }),
});
const data = await res.json();
console.log(data.sid, data.status);`,
    python: `import requests

res = requests.post(
    "${base}/calls",
    headers={"Authorization": "Bearer SUA_CHAVE"},
    json={
        "to": "+5519999998888",
        "from": "+5519888887777",
        "applicationId": "app_xxx",
        "variables": {"nome": "Maria"},
    },
)
print(res.json())`,
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <Typography className={classes.title}>Visão geral da API</Typography>
        <Typography className={classes.subtitle}>
          API REST nativa para fazer chamadas e disparar fluxos. Autenticação por chave de API, sem o flow engine no caminho.
        </Typography>

        {/* base + auth */}
        <div className={classes.section}>
          <div className={classes.h}>Endpoint base</div>
          <div className={classes.card}>
            <div className={classes.kv}>
              <span className={classes.kvK}>Base URL</span>
              <span className={classes.kvV}>{base}</span>
              <IconButton size="small" className={classes.copy} onClick={() => copy(base)} title="Copiar">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.kv}>
              <span className={classes.kvK}>Autenticação</span>
              <span className={classes.kvV}>Authorization: Bearer SUA_CHAVE</span>
              <IconButton size="small" className={classes.copy} onClick={() => copy("Authorization: Bearer SUA_CHAVE")} title="Copiar">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.kv}>
              <span className={classes.kvK}>Teste rápido</span>
              <span className={classes.kvV}>GET {base}/me</span>
              <IconButton size="small" className={classes.copy} onClick={() => copy(`${base}/me`)} title="Copiar">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>

        {/* quickstart */}
        <div className={classes.section}>
          <div className={classes.h}>Começar em 3 passos</div>
          <div className={classes.card}>
            <ol className={classes.steps}>
              <li className={classes.step}>
                Crie uma <b>chave de API</b> com o escopo <code>calls.create</code> em Chaves de API.
              </li>
              <li className={classes.step}>
                Publique uma automação com o gatilho <b>"Ao atender chamada"</b> e copie o código dela (<code>app_…</code>).
              </li>
              <li className={classes.step}>
                Chame <code>POST /v1/calls</code> com <code>to</code>, <code>from</code> (um número seu) e o <code>applicationId</code>.
              </li>
            </ol>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Button size="small" variant="contained" color="primary" startIcon={<KeyIcon />} onClick={() => history.push("/developers/keys")}>
                Criar chave de API
              </Button>
              <Button size="small" variant="outlined" color="secondary" endIcon={<OpenInNewIcon />} href="/docs" target="_blank">
                Documentação completa
              </Button>
            </div>
          </div>
        </div>

        {/* code sample */}
        <div className={classes.section}>
          <div className={classes.h}>Fazer uma chamada</div>
          <div>
            <div className={classes.tabs}>
              {["curl", "node", "python"].map(l => (
                <div key={l} className={`${classes.tab} ${lang === l ? classes.tabOn : ""}`} onClick={() => setLang(l)}>
                  {l === "node" ? "Node.js" : l === "python" ? "Python" : "cURL"}
                </div>
              ))}
            </div>
            <pre className={classes.code}>
              {snippets[lang]}
              <IconButton size="small" className={classes.codeCopy} onClick={() => copy(snippets[lang])} title="Copiar">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </pre>
          </div>
        </div>

        {/* scopes */}
        <div className={classes.section}>
          <div className={classes.h}>Escopos disponíveis</div>
          <div className={classes.scopes}>
            {scopes.map(s => (
              <div key={s.key} className={classes.scope}>
                {s.label}
                <span className={classes.scopeKey}>{s.key}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
